import { cn } from '@/lib/utils';
import { isValidDate } from '@aldebaran/common/util/date';
import { Patient } from '@aldebaran/prisma';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { ReactComponent as QuestionMarkIcon } from '@/assets/Onboarding/QuestionMarkIcon.svg';
import { Button } from '@/components/ui/button';
import { ResponseDOBField } from '@/components/ResponseDOBField';
import { ResponseTextField } from '@/components/ResponseTextField';
import { usePatientDataCollectionConfig } from '@/hooks/usePatientDataCollectionConfig';
import { FieldHelper, FieldLabel } from '@/components/PatientData/FieldHelper';
import { useQuestionnaireNavigation } from '@/context/NavigationProvider';
import {
  useCreatePatient,
  useSelectPatient,
  useUpdatePatient,
} from '@/hooks/patient.hooks';
import { useInjectInterlocuter } from '@/hooks/useInjectInterlocuter';
import { useStatistics } from '@/hooks/useStatistics';
import { buildOnboardingContext } from '@/onboarding/orchestration/buildContext';
import { PatientPageLayout } from '@/components/PatientPageLayout';
import { SexeModale } from '@/pages/onboarding/med-g/SexeModale';
import { RootState } from '@/types/graph';
import { computeSelf, isUnder16 } from '@/util/patient';
import { isWebsiteDemoPractice } from '@/util/demoPractice';
import { loadDemoLead } from '@/components/DemoLead/demoLeadStorage';
import { isKioskOnboarding } from '@/util/kiosk';
import { scrollToTop } from '@/util/scroll';

type PatientInfoForm = Pick<
  Patient,
  'firstName' | 'lastName' | 'dateOfBirth' | 'sex'
>;

type PatientInfoFormErrors = {
  [key in keyof PatientInfoForm]: boolean;
};

type ExtraIdentityForm = Pick<Patient, 'birthName'>;

const REQUIRED_FIELD_MESSAGE = 'Ce champ est obligatoire';

export const PatientInformation = () => {
  const patient = useSelector((state: RootState) => state.consultation.patient);
  const invitation = useSelector((state: RootState) => state.invitationId);
  const searchParams = useSelector((state: RootState) => state.searchParams);
  const interlocuter = useSelector(
    (state: RootState) => state.questionnaire.interlocuter,
  );
  const assistedByPro = useSelector(
    (state: RootState) => state.graph.options?.ASSISTED_BY_PRO ?? false,
  );
  const dataCollectionConfig = usePatientDataCollectionConfig();

  const patientOnboarding = useSelector(
    (state: RootState) => state.questionnaire.config?.patientOnboarding,
  );
  const isKiosk = isKioskOnboarding(patientOnboarding);

  const isPatientInformationComplete = useSelector(
    (state: RootState) =>
      buildOnboardingContext(state).isPatientInformationComplete,
  );
  const isDemoPracticeRun = useSelector((state: RootState) =>
    isWebsiteDemoPractice(state.consultation.practice?.slug),
  );
  const injectInterlocuter = useInjectInterlocuter();

  const [urlSearchParams] = useSearchParams();

  const shouldForceEditionMode = useMemo(
    () =>
      urlSearchParams.get('edit') === 'true' ||
      urlSearchParams.get('goToEditPage') === 'true' ||
      searchParams['edit'] === 'true' ||
      searchParams['goToEditPage'] === 'true',
    [searchParams, urlSearchParams],
  );

  const configLoaded = useSelector((s: RootState) => !!s.questionnaire.config);

  const [mode, setMode] = useState<'edition' | 'validation'>(() => {
    if (shouldForceEditionMode || !configLoaded) return 'edition';
    return isPatientInformationComplete ? 'validation' : 'edition';
  });

  // When the variant config isn't in the store yet at mount, kiosk-ness reads as
  // false and a name-only kiosk patient scores incomplete. Re-evaluate once the
  // config lands — once only, so this can never override switchMode.
  const modeSettled = useRef(configLoaded);
  useEffect(() => {
    if (modeSettled.current || !configLoaded) return;
    modeSettled.current = true;
    if (shouldForceEditionMode) return;
    setMode(isPatientInformationComplete ? 'validation' : 'edition');
  }, [configLoaded]);

  const recordStatistics = useStatistics();
  const { navigateNext } = useQuestionnaireNavigation();

  const { mutate: selectPatient, isLoading: isLoadingSelect } =
    useSelectPatient();
  const { mutate: createPatient, isLoading: isLoadingCreate } =
    useCreatePatient(true);
  const { mutate: updatePatient, isLoading: isLoadingUpdate } =
    useUpdatePatient();

  const [isSexeModalOpen, setIsSexeModalOpen] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [form, setForm] = useState<PatientInfoForm>(() => {
    // Website demo: prefill with the name typed in the entry lead modal
    const demoLead = !patient && isDemoPracticeRun ? loadDemoLead() : null;
    return {
      firstName: patient?.firstName ?? demoLead?.firstName ?? '',
      lastName: patient?.lastName ?? demoLead?.lastName ?? '',
      dateOfBirth: patient?.dateOfBirth ?? null,
      sex: patient?.sex ?? null,
    };
  });
  const [errors, setErrors] = useState<PatientInfoFormErrors>({
    firstName: false,
    lastName: false,
    dateOfBirth: false,
    sex: false,
  });
  const [extra, setExtra] = useState<ExtraIdentityForm>({
    birthName: patient?.birthName ?? null,
  });
  const [extraErrors, setExtraErrors] = useState<{ birthName?: string }>({});

  const editMode = patient ? 'edit' : 'add';
  const loading = isLoadingSelect || isLoadingCreate || isLoadingUpdate;

  const showBirthName = dataCollectionConfig.BIRTH_NAME.enabled;

  const validateExtra = (nextExtra: ExtraIdentityForm) => {
    const errors: typeof extraErrors = {};

    if (
      showBirthName &&
      dataCollectionConfig.BIRTH_NAME.required &&
      !nextExtra.birthName?.trim()
    ) {
      errors.birthName = REQUIRED_FIELD_MESSAGE;
    }

    return errors;
  };

  const isExtraValid = Object.keys(validateExtra(extra)).length === 0;

  const Subtitle = ({ className }: { className?: string }) => (
    <p className={cn('nxs:text-center font-light text-sm mt-2', className)}>
      {isKiosk ? (
        'Pour assurer l’identité du patient au médecin'
      ) : (
        <>
          Pour assurer l’identité du patient au médecin et <br />
          ajuster les questions selon l&apos;âge et le sexe
        </>
      )}
    </p>
  );

  useEffect(() => {
    if (!patient) return;
    setForm({
      firstName: patient.firstName ?? '',
      lastName: patient.lastName ?? '',
      dateOfBirth: patient.dateOfBirth ?? null,
      sex: patient.sex ?? null,
    });
    setExtra({ birthName: patient.birthName ?? null });
  }, [patient]);

  useEffect(() => {
    scrollToTop();
  }, []);

  const validateForm = (nextForm: PatientInfoForm) => {
    const nextErrors: PatientInfoFormErrors = {
      firstName: false,
      lastName: false,
      dateOfBirth: false,
      sex: false,
    };

    if (!nextForm.firstName?.trim()) nextErrors.firstName = true;
    if (!nextForm.lastName?.trim()) nextErrors.lastName = true;
    if (!isKiosk) {
      if (!nextForm.dateOfBirth) nextErrors.dateOfBirth = true;
      if (!nextForm.sex) nextErrors.sex = true;
    }

    const valid = !Object.values(nextErrors).some((value) => value);
    return { valid, errors: nextErrors };
  };

  const runExitTransition = (callback: () => void) => {
    setIsExiting(true);
    setTimeout(() => {
      callback();
    }, 300);
  };

  const switchMode = (
    nextMode: 'edition' | 'validation',
    errorFlags?: { extraErrors?: typeof extraErrors },
  ) => {
    runExitTransition(() => {
      setMode(nextMode);
      setIsExiting(false);
      if (errorFlags?.extraErrors) {
        setExtraErrors(errorFlags.extraErrors);
      }
      scrollToTop();
    });
  };

  const handleEditionValidateClick = () => {
    const validation = validateForm(form);
    const nextExtraErrors = validateExtra(extra);
    const hasExtraErrors = Object.keys(nextExtraErrors).length > 0;
    if (!validation.valid || hasExtraErrors) {
      if (!validation.valid) setErrors(validation.errors);
      setExtraErrors(nextExtraErrors);
      return;
    }

    const self = computeSelf({
      selfURLParam: searchParams['self'],
      interlocuter,
      dateOfBirth: form.dateOfBirth,
      assistedByPro,
    });

    injectInterlocuter(self);

    const namePayload = {
      firstName: form.firstName?.trim() ?? null,
      lastName: form.lastName?.trim() ?? null,
    };

    // Only send the fields the practice collects, so a disabled field never
    // overwrites a value entered elsewhere (pro app, previous consultation).
    const extraPayload = {
      ...(showBirthName && { birthName: extra.birthName?.trim() || null }),
    };

    const onSuccess = (section: string) => (savedPatient: Patient) => {
      selectPatient(savedPatient.id, {
        onSuccess: () => {
          recordStatistics({ section });
          navigateNext();
        },
      });
    };

    if (editMode === 'add') {
      createPatient(
        { ...form, ...namePayload, ...extraPayload, self },
        { onSuccess: onSuccess('PATIENT_CREATED') },
      );
      return;
    }

    if (editMode === 'edit' && patient) {
      updatePatient(
        { id: patient.id, ...form, ...namePayload, ...extraPayload, self },
        { onSuccess: onSuccess('PATIENT_UPDATED') },
      );
    }
  };

  const handleValidationClick = () => {
    if (!patient?.id) return;

    // A required extra field the stored patient is missing (newly enabled by
    // the practice) sends the patient back to edition rather than through.
    const missingExtraErrors = validateExtra({
      birthName: patient.birthName ?? null,
    });
    if (Object.keys(missingExtraErrors).length > 0) {
      switchMode('edition', { extraErrors: missingExtraErrors });
      return;
    }

    injectInterlocuter(
      computeSelf({
        selfURLParam: searchParams['self'],
        interlocuter,
        dateOfBirth: patient.dateOfBirth,
        assistedByPro,
      }),
    );

    if (patient.dateOfBirth && isUnder16(patient.dateOfBirth)) {
      updatePatient(
        { id: patient.id, self: false },
        {
          onSuccess: (updatedPatient) => {
            selectPatient(updatedPatient.id, {
              onSuccess: () => {
                navigateNext();
              },
            });
          },
        },
      );
      return;
    }

    // Re-inject the patient so DATE_OF_BIRTH and SEX assertions are
    // recorded in the graph before CONSULTATION_TIME is (re-)evaluated.
    // INTERLOCUTER is recorded explicitly above.
    selectPatient(patient.id, {
      onSuccess: () => {
        navigateNext();
      },
    });
  };

  return (
    <div
      className={cn(
        'h-full flex flex-col transition-opacity duration-300 ease-in-out',
        isExiting ? 'opacity-0' : 'opacity-100',
      )}
    >
      {mode === 'edition' ? (
        <>
          <PatientPageLayout
            columnClassName="w-full"
            title={
              <>
                {invitation ? (
                  <p className="text-2xl min-[23rem]:text-3.5xl font-semibold nxs:text-center">
                    {interlocuter === 'SELF'
                      ? 'Mettez à jour vos informations'
                      : 'Mettez à jour les informations du patient'}
                  </p>
                ) : (
                  <span className="flex flex-col">
                    <p className="text-2xl min-[23rem]:text-3.5xl font-semibold nxs:text-center">
                      {interlocuter === 'SELF' ? (
                        <>Rentrez vos informations</>
                      ) : (
                        <>
                          Rentrez les informations <br />
                          du patient
                        </>
                      )}
                    </p>
                    <Subtitle />
                  </span>
                )}
              </>
            }
            footer={
              <div className="flex flex-col w-full items-center">
                <Button
                  variant="ctaPrimary"
                  size="lg"
                  onClick={handleEditionValidateClick}
                  disabled={
                    loading || !validateForm(form).valid || !isExtraValid
                  }
                >
                  Valider
                </Button>
              </div>
            }
          >
            <div className="flex flex-col w-full items-center gap-3 py-4 z-10">
              <div className="flex flex-col w-full gap-4 max-w-86">
                <div className="flex flex-col gap-1">
                  <p className="text-xs font-medium uppercase tracking-[0.02em] text-gray-500">
                    Prénom
                  </p>
                  <ResponseTextField
                    label="Prénom"
                    value={form.firstName || ''}
                    onChange={(value) => {
                      setForm((current) => ({
                        ...current,
                        firstName: value || '',
                      }));
                      setErrors((current) => ({
                        ...current,
                        firstName: false,
                      }));
                    }}
                    error={errors.firstName}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <p className="text-xs font-medium uppercase tracking-[0.02em] text-gray-500">
                    Nom
                  </p>
                  <ResponseTextField
                    label="Nom de famille"
                    value={form.lastName || ''}
                    onChange={(value) => {
                      setForm((current) => ({
                        ...current,
                        lastName: value || '',
                      }));
                      setErrors((current) => ({ ...current, lastName: false }));
                    }}
                    error={errors.lastName}
                  />
                </div>
                {showBirthName && (
                  <div className="flex flex-col gap-1">
                    <FieldLabel
                      optional={!dataCollectionConfig.BIRTH_NAME.required}
                    >
                      Nom de naissance
                    </FieldLabel>
                    <ResponseTextField
                      label="Nom de naissance"
                      value={extra.birthName || ''}
                      onChange={(value) => {
                        setExtra((current) => ({
                          ...current,
                          birthName: value || null,
                        }));
                        setExtraErrors((current) => ({
                          ...current,
                          birthName: undefined,
                        }));
                      }}
                      error={!!extraErrors.birthName}
                    />
                    <FieldHelper error>{extraErrors.birthName}</FieldHelper>
                  </div>
                )}
                {!isKiosk && (
                  <>
                    <div className="flex flex-col gap-1">
                      <p className="text-xs font-medium uppercase tracking-[0.02em] text-gray-500">
                        Date de naissance
                      </p>
                      <ResponseDOBField
                        label="jj/mm/aaaa"
                        value={
                          isValidDate(form.dateOfBirth)
                            ? new Date(form.dateOfBirth).getTime()
                            : undefined
                        }
                        onChange={(date) => {
                          setForm((current) => ({
                            ...current,
                            dateOfBirth: isValidDate(date)
                              ? new Date(date)
                              : null,
                          }));
                          setErrors((current) => ({
                            ...current,
                            dateOfBirth: false,
                          }));
                        }}
                        disabled={false}
                        withIcon={false}
                        error={errors.dateOfBirth}
                      />
                    </div>
                    <div className="flex flex-col">
                      <div className="flex items-center gap-1">
                        <p className="text-xs font-medium uppercase tracking-[0.02em] text-gray-500">
                          Sexe biologique
                        </p>
                        <button
                          type="button"
                          className="inline-flex size-6 shrink-0 cursor-pointer items-center justify-center text-gray-500"
                          onClick={() => setIsSexeModalOpen(true)}
                          aria-label="Plus d’informations sur le sexe biologique"
                        >
                          <QuestionMarkIcon className="size-3" aria-hidden />
                        </button>
                      </div>

                      <div
                        className={cn(
                          'relative flex items-stretch rounded-1.5xl border p-1',
                          errors.sex
                            ? 'bg-red-25 border-red-300'
                            : 'bg-white border-gray-200',
                        )}
                      >
                        {/* Sliding indicator, only for animation purposes */}
                        <div
                          className={cn(
                            'absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-lg border border-indigo-800 bg-blue-50 transition-all duration-300 ease-in-out',
                            form.sex === null
                              ? 'opacity-0 left-1'
                              : form.sex === 'MALE'
                              ? 'opacity-100 left-1'
                              : 'opacity-100 left-[calc(50%+2px)]',
                          )}
                        />
                        <Button
                          variant="toggleSegment"
                          size="md"
                          type="button"
                          data-selected={false}
                          className={cn(
                            'relative z-10 rounded-lg',
                            form.sex === 'MALE' && 'font-semibold',
                          )}
                          onClick={() => {
                            setForm((current) => ({
                              ...current,
                              sex: current.sex === 'MALE' ? null : 'MALE',
                            }));
                            setErrors((current) => ({
                              ...current,
                              sex: false,
                            }));
                          }}
                        >
                          Masculin
                        </Button>
                        {form.sex === null && (
                          <div
                            className="shrink-0 w-px self-stretch bg-gray-200 z-10"
                            role="presentation"
                          />
                        )}
                        <Button
                          variant="toggleSegment"
                          size="md"
                          type="button"
                          data-selected={false}
                          className={cn(
                            'relative z-10 rounded-lg',
                            form.sex === 'FEMALE' && 'font-semibold',
                          )}
                          onClick={() => {
                            setForm((current) => ({
                              ...current,
                              sex: current.sex === 'FEMALE' ? null : 'FEMALE',
                            }));
                            setErrors((current) => ({
                              ...current,
                              sex: false,
                            }));
                          }}
                        >
                          Féminin
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </PatientPageLayout>
        </>
      ) : (
        <>
          <PatientPageLayout
            columnClassName="w-full"
            title={
              <>
                <p className="text-2xl min-[23rem]:text-3.5xl font-semibold nxs:text-center">
                  {interlocuter === 'SELF' ? (
                    <>
                      Vos informations <br /> sont-elles exactes ?
                    </>
                  ) : (
                    <>
                      Les informations du <br /> patient sont-elles exactes ?
                    </>
                  )}
                </p>
                <Subtitle className="mb-3" />
              </>
            }
            footer={
              <div className="flex flex-col w-full items-center gap-1">
                <Button
                  variant="ctaPrimary"
                  size="lg"
                  onClick={handleValidationClick}
                  disabled={loading}
                >
                  Oui, c&apos;est exact
                </Button>
                <Button
                  variant="noBorder"
                  size="lg"
                  onClick={() => switchMode('edition')}
                  disabled={loading}
                >
                  Non, je modifie
                </Button>
              </div>
            }
          >
            <div className="flex flex-col w-full items-center gap-3 py-4 z-10">
              <div className="flex flex-col w-full max-w-[342px] gap-[18px] rounded-[24px] bg-white/65 p-6">
                <div className="flex flex-col gap-1">
                  <p className="text-2xs font-semibold uppercase leading-4 tracking-[0.02em] text-gray-500">
                    Nom
                  </p>
                  <p className="font-semibold">
                    {patient?.firstName} {patient?.lastName?.toUpperCase()}
                  </p>
                </div>
                {showBirthName && patient?.birthName && (
                  <>
                    <div className="h-px w-full bg-gray-200" role="separator" />
                    <div className="flex flex-col gap-1">
                      <p className="text-2xs font-semibold uppercase leading-4 tracking-[0.02em] text-gray-500">
                        Nom de naissance
                      </p>
                      <p className="font-semibold">{patient.birthName}</p>
                    </div>
                  </>
                )}
                {!isKiosk && (
                  <>
                    <div className="h-px w-full bg-gray-200" role="separator" />
                    <div className="flex flex-col gap-1">
                      <p className="text-2xs font-semibold uppercase leading-4 tracking-[0.02em] text-gray-500">
                        Date de naissance
                      </p>
                      <p className="font-semibold">
                        {patient?.dateOfBirth
                          ? new Date(
                              patient.dateOfBirth as unknown as string,
                            ).toLocaleDateString('fr-FR')
                          : ''}
                      </p>
                    </div>
                    <div className="h-px w-full bg-gray-200" role="separator" />
                    <div className="flex flex-col">
                      <div className="flex items-center gap-1 ">
                        <p className="text-2xs font-semibold uppercase leading-4 tracking-[0.02em] text-gray-500">
                          Sexe biologique
                        </p>
                        <button
                          type="button"
                          className="inline-flex size-6 shrink-0 cursor-pointer items-center justify-center text-gray-500"
                          onClick={() => setIsSexeModalOpen(true)}
                          aria-label="Plus d’informations sur le sexe biologique"
                        >
                          <QuestionMarkIcon
                            className="mb-0.5 size-3"
                            aria-hidden
                          />
                        </button>
                      </div>
                      <p className="font-semibold">
                        {patient?.sex === 'MALE' && 'Masculin'}
                        {patient?.sex === 'FEMALE' && 'Féminin'}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </PatientPageLayout>
        </>
      )}

      {isSexeModalOpen && (
        <SexeModale
          show={isSexeModalOpen}
          onClose={() => setIsSexeModalOpen(false)}
        />
      )}
    </div>
  );
};
